import { Box } from "@/Components/v2/atoms/Box";
import { Stack } from "@/Components/v2/atoms/Stack";
import { GridCellParams } from "@mui/x-data-grid-premium";

import { NewActionPlanModal } from "../../components/NewActionPlanModal/NewActionPlanModal";

import { CircularProgress } from "@/Components/v2/atoms/Progress";
import { styled } from "@mui/material";
import { DataGrid } from "@/Components/v2/organism/DataGrid";
import { useDeleteActionPlan } from "@/pages/scenario-builder/v3/api/v2/deleteActionPlan";
import { useActionPlans } from "@/pages/scenario-builder/v3/api/v2/getActionPlans";
import ScenarioBuilderHeader from "@/pages/scenario-builder/v3/components/ScenarioBuilderHeader";
import { useScenariosV3Routes } from "@/pages/scenario-builder/v3/hooks/useScenariosV3Routes";
import { ActionPlanSummary } from "@mutiny-group/sbo-openapi-client";
import { confirmDeleteActionPlan } from "./components/DeleteActionPlanConfirmationModal";
import NewRecommendations from "./components/NewRecommendations";
import { getColumnDefinitions } from "./helpers/getColumnDefinitions";
import { useState } from "react";
import DataRecencyBanner from "./components/DataRecencyBanner";
import { useDateRanges } from "../../api/getDateRanges";
import NoActionPlansCTA from "./components/NoActionPlans";
import { useGeoEnabled } from "../../hooks/useGeoEnabled";

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-root": {
    border: "none",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: theme.palette.tableColumnHeader.background,
  },
  "& .MuiDataGrid-row": {
    cursor: "pointer",
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
  },
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
    outline: "none",
  },
  "& .MuiDataGrid-columnHeader:last-child": {
    paddingRight: "0px",
  },
  "& .MuiDataGrid-cell:last-child": {
    paddingRight: "0px",
  },
}));

const ActionPlansPage = () => {
  const { data: actionPlansData, isLoading } = useActionPlans();
  const { data: dateRanges, isLoading: isDateRangesLoading } = useDateRanges({
    isScenariosApiEnabled: true,
  });
  const { navigateToActionPlan } = useScenariosV3Routes();
  const { mutate: deleteActionPlan } = useDeleteActionPlan();
  const geoEnabled = useGeoEnabled();
  const isGeoEnabled = geoEnabled.isLoading ? false : geoEnabled.isEnabled;

  // Modal state management
  const [newActionPlanOpen, setNewActionPlanOpen] = useState(false);

  const { actionPlans, shouldShowNewRecommendations } = actionPlansData || {};
  const showCTA = !actionPlans || actionPlans?.length === 0;

  if (isLoading) return <CircularProgress size={150} sx={{ marginTop: "30rem" }} />;

  const handleDelete = async (actionPlanUuid: string, actionPlanName: string) => {
    const confirmed = await confirmDeleteActionPlan({ actionPlanName });
    if (confirmed) {
      deleteActionPlan(actionPlanUuid);
    }
  };

  const columns = getColumnDefinitions({ onDelete: handleDelete, isGeoEnabled });

  return (
    <Box>
      <Stack spacing={3} mb={2} paddingX={4} paddingTop={2} paddingBottom={2}>
        <ScenarioBuilderHeader />
        {showCTA ? (
          <>
            <NoActionPlansCTA onClick={() => setNewActionPlanOpen(true)} />
            <NewActionPlanModal
              open={newActionPlanOpen}
              handleClose={() => setNewActionPlanOpen(false)}
            />
          </>
        ) : (
          <>
            <Stack direction="column" gap={2}>
              {!isDateRangesLoading && dateRanges?.startDate && (
                <DataRecencyBanner date={dateRanges.startDate} />
              )}
              {shouldShowNewRecommendations && <NewRecommendations />}
            </Stack>
            <StyledDataGrid
              rows={actionPlans || []}
              columns={columns}
              getRowId={(row) => row.uuid}
              pagination
              pageSizeOptions={[15, 30, 50]}
              density="compact"
              onCellClick={(e: GridCellParams<ActionPlanSummary>) => {
                if (e.field === "actions") {
                  return;
                }
                navigateToActionPlan(e.row.uuid);
              }}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 15 },
                },
                sorting: {
                  sortModel: [{ field: "createdAt", sort: "desc" }],
                },
              }}
              sx={{
                "& .MuiDataGrid-overlayWrapper": {
                  height: "auto !important",
                },
                "& .MuiDataGrid-overlayWrapperInner": {
                  height: "auto !important",
                },
              }}
            />
          </>
        )}
      </Stack>
    </Box>
  );
};

export default ActionPlansPage;
